import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import AgentService from '../../services/agentService';
import { supabase } from '../../config/supabase';

interface Secret {
  type: string;
  secret_id: string;
  name: string;
  used_by?: {
    tools?: any[];
    agent_tools?: any[];
    others?: string[];
  };
}

interface SettingsProps {
  apiKey: string;
}

const Settings: React.FC<SettingsProps> = ({ apiKey }) => {
  const [secrets, setSecrets] = useState<Secret[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showSecretForm, setShowSecretForm] = useState(false);
  const [newSecret, setNewSecret] = useState({
    name: '',
    value: '',
  });
  const [dashboardSettings, setDashboardSettings] = useState<any>(null);
  const [analyticsData, setAnalyticsData] = useState<any>({
    conversations: [],
    agents: [],
    metrics: null,
    loading: true
  });

  const fetchSecrets = useCallback(async () => {
    if (!apiKey) return;
    
    try {
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Get user's secret IDs from our tracking table
      const { data: userSecrets, error: dbError } = await supabase
        .from('user_secrets')
        .select('*');

      if (dbError) {
        console.error('Error fetching user secrets from database:', dbError);
        setSecrets([]);
        return;
      }

      if (!userSecrets || userSecrets.length === 0) {
        setSecrets([]);
        return;
      }

      // Fetch the actual secret details from ElevenLabs for user's secrets
      const secretDetails: Secret[] = [];
      
      for (const userSecret of userSecrets) {
        try {
          const response = await fetch(`https://api.elevenlabs.io/v1/convai/secrets/${userSecret.elevenlabs_secret_id}`, {
            headers: {
              'xi-api-key': apiKey,
              'Content-Type': 'application/json',
            },
          });

          if (response.ok) {
            const secretData = await response.json();
            secretDetails.push({
              ...secretData,
              // Override with our stored data for consistency
              name: userSecret.name
            });
          } else {
            // Secret might have been deleted from ElevenLabs, clean up our database
            console.warn(`Secret ${userSecret.elevenlabs_secret_id} not found in ElevenLabs, removing from database`);
            await supabase
              .from('user_secrets')
              .delete()
              .eq('elevenlabs_secret_id', userSecret.elevenlabs_secret_id);
          }
        } catch (error) {
          console.error(`Error fetching secret ${userSecret.elevenlabs_secret_id}:`, error);
        }
      }
      
      setSecrets(secretDetails);
    } catch (err) {
      console.error('Failed to fetch secrets:', err);
    }
  }, [apiKey]);

  const fetchDashboardSettings = useCallback(async () => {
    if (!apiKey) return;
    
    try {
      const response = await fetch('https://api.elevenlabs.io/v1/convai/settings/dashboard', {
        headers: {
          'xi-api-key': apiKey,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch dashboard settings: ${response.statusText}`);
      }

      const data = await response.json();
      setDashboardSettings(data);
    } catch (err) {
      console.error('Failed to fetch dashboard settings:', err);
    }
  }, [apiKey]);

  const fetchAnalyticsData = useCallback(async () => {
    if (!apiKey) return;
    
    setAnalyticsData((prev: any) => ({ ...prev, loading: true }));
    
    try {
      const agentService = new AgentService(apiKey);
      
      // Fetch user's conversations (filtered by their agents)
      const conversations = await agentService.getUserConversations();
      
      // Fetch user's agents
      const agents = await agentService.getUserAgents();

      // Fetch usage data for the last 30 days
      const endTime = Date.now();
      const startTime = endTime - (30 * 24 * 60 * 60 * 1000); // 30 days ago
      
      let usageData = null;
      try {
        const usageResponse = await fetch(`https://api.elevenlabs.io/v1/usage/character-stats?start_unix=${startTime}&end_unix=${endTime}&breakdown_type=voice&aggregation_interval=day&metric=credits`, {
          headers: {
            'xi-api-key': apiKey,
            'Content-Type': 'application/json',
          },
        });

        if (usageResponse.ok) {
          usageData = await usageResponse.json();
        }
      } catch (error) {
        console.error('Error fetching usage data:', error);
      }

      // Calculate metrics from the actual data
      const metrics = calculateMetrics(conversations, usageData);
      
      setAnalyticsData({
        conversations,
        agents,
        metrics,
        usage: usageData,
        loading: false
      });
    } catch (err) {
      console.error('Failed to fetch analytics data:', err);
      setAnalyticsData((prev: any) => ({ ...prev, loading: false }));
    }
  }, [apiKey]);

  const calculateMetrics = (conversations: any[], usageData: any) => {
    if (conversations.length === 0) {
      return {
        totalCalls: 0,
        successRate: 0,
        averageDuration: 0,
        dailyData: [],
        statusDistribution: [],
        responseTimeData: [],
        monthlyData: [],
        topAgents: [],
        peakHours: [],
        usageData: {
          totalCredits: 0,
          dailyUsage: [],
          voiceBreakdown: [],
          averageCreditsPerCall: 0
        },
        costData: {
          costPerCall: 0,
          monthlyTotal: 0,
          savings: 0
        }
      };
    }

    const totalCalls = conversations.length;
    const successfulCalls = conversations.filter(c => c.call_successful === 'success' || c.status === 'completed').length;
    const successRate = totalCalls > 0 ? (successfulCalls / totalCalls) * 100 : 0;
    
    const totalDuration = conversations.reduce((sum, c) => sum + (c.call_duration_secs || 0), 0);
    const averageDuration = totalCalls > 0 ? totalDuration / totalCalls : 0;

    // Process usage data - only if available
    let usageMetrics = {
      totalCredits: 0,
      dailyUsage: [] as Array<{ date: string; credits: number; timestamp: number }>,
      voiceBreakdown: [] as Array<{ voice: string; credits: number }>,
      averageCreditsPerCall: 0
    };

    if (usageData && usageData.time && usageData.usage) {
      // Calculate total credits
      const allUsage = usageData.usage.All || [];
      usageMetrics.totalCredits = allUsage.reduce((sum: number, val: number) => sum + val, 0);
      
      // Create daily usage data
      usageMetrics.dailyUsage = usageData.time.map((timestamp: number, index: number) => {
        const date = new Date(timestamp);
        const totalUsageForDay = Object.values(usageData.usage).reduce((sum: number, usageArray: any) => {
          return sum + (usageArray[index] || 0);
        }, 0);
        
        return {
          date: date.toLocaleDateString('en', { month: 'short', day: 'numeric' }),
          credits: totalUsageForDay,
          timestamp
        };
      }).slice(-7); // Last 7 days

      // Create voice breakdown data
      usageMetrics.voiceBreakdown = Object.entries(usageData.usage)
        .filter(([voice]) => voice !== 'All')
        .map(([voice, usage]) => ({
          voice,
          credits: (usage as number[]).reduce((sum, val) => sum + val, 0)
        }))
        .sort((a, b) => b.credits - a.credits)
        .slice(0, 5); // Top 5 voices

      // Calculate average credits per call
      usageMetrics.averageCreditsPerCall = totalCalls > 0 ? usageMetrics.totalCredits / totalCalls : 0;
    }

    // Group conversations by day for the last 7 days
    const last7Days = Array.from({ length: 7 }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - (6 - i));
      return date;
    });

    const dailyData = last7Days.map(date => {
      const dayStart = new Date(date.setHours(0, 0, 0, 0)).getTime() / 1000;
      const dayEnd = new Date(date.setHours(23, 59, 59, 999)).getTime() / 1000;
      
      const dayConversations = conversations.filter(c => 
        c.start_time_unix_secs >= dayStart && c.start_time_unix_secs <= dayEnd
      );
      
      const successfulDay = dayConversations.filter(c => 
        c.call_successful === 'success' || c.status === 'completed'
      ).length;

      return {
        day: date.toLocaleDateString('en', { weekday: 'short' }),
        calls: dayConversations.length,
        successful: successfulDay
      };
    });

    // Status distribution - only if status data exists
    const statusCounts = conversations.reduce((acc, c) => {
      if (c.status) {
        const status = c.status === 'completed' ? 'Completed' : 
                     c.status === 'failed' ? 'Failed' : 'Dropped';
        acc[status] = (acc[status] || 0) + 1;
      }
      return acc;
    }, {} as Record<string, number>);

    const statusDistribution = Object.entries(statusCounts).map(([name, value]) => ({
      name,
      value,
      color: name === 'Completed' ? '#10b981' : name === 'Failed' ? '#ef4444' : '#f59e0b'
    }));

    // Response time analysis (only if duration data exists)
    const responseTimeData = conversations.some(c => c.call_duration_secs !== undefined) ? [
      { time: '<1m', count: conversations.filter(c => c.call_duration_secs && c.call_duration_secs < 60).length },
      { time: '1-2m', count: conversations.filter(c => c.call_duration_secs && c.call_duration_secs >= 60 && c.call_duration_secs < 120).length },
      { time: '2-5m', count: conversations.filter(c => c.call_duration_secs && c.call_duration_secs >= 120 && c.call_duration_secs < 300).length },
      { time: '5-10m', count: conversations.filter(c => c.call_duration_secs && c.call_duration_secs >= 300 && c.call_duration_secs < 600).length },
      { time: '>10m', count: conversations.filter(c => c.call_duration_secs && c.call_duration_secs >= 600).length },
    ] : [];

    // Monthly data (last 4 months) - only calls, no simulated satisfaction
    const monthlyData = Array.from({ length: 4 }, (_, i) => {
      const date = new Date();
      date.setMonth(date.getMonth() - (3 - i));
      const monthStart = new Date(date.getFullYear(), date.getMonth(), 1).getTime() / 1000;
      const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59).getTime() / 1000;
      
      const monthConversations = conversations.filter(c => 
        c.start_time_unix_secs >= monthStart && c.start_time_unix_secs <= monthEnd
      );
      
      return {
        month: date.toLocaleDateString('en', { month: 'short' }),
        calls: monthConversations.length
      };
    });

    // Top performing agents - only if agent data exists
    const agentStats = conversations.reduce((acc, c) => {
      if (c.agent_name || c.agent_id) {
        const agentName = c.agent_name || c.agent_id || 'Unknown Agent';
        if (!acc[agentName]) {
          acc[agentName] = { total: 0, successful: 0 };
        }
        acc[agentName].total++;
        if (c.call_successful === 'success' || c.status === 'completed') {
          acc[agentName].successful++;
        }
      }
      return acc;
    }, {} as Record<string, { total: number; successful: number }>);

    const topAgents = Object.entries(agentStats)
      .map(([name, stats]) => {
        const { total, successful } = stats as { total: number; successful: number };
        return {
          name,
          successRate: total > 0 ? ((successful / total) * 100).toFixed(1) : '0.0'
        };
      })
      .sort((a, b) => parseFloat(b.successRate) - parseFloat(a.successRate))
      .slice(0, 3);

    // Peak hours analysis - only if timestamp data exists
    const hourlyStats = conversations.reduce((acc, c) => {
      if (c.start_time_unix_secs) {
        const hour = new Date(c.start_time_unix_secs * 1000).getHours();
        acc[hour] = (acc[hour] || 0) + 1;
      }
      return acc;
    }, {} as Record<number, number>);

    const peakHours = Object.entries(hourlyStats)
      .map(([hour, count]) => ({
        timeRange: `${hour}:00 - ${parseInt(hour) + 1}:00`,
        calls: count as number
      }))
      .sort((a, b) => b.calls - a.calls)
      .slice(0, 3);

    return {
      totalCalls,
      successRate,
      averageDuration,
      dailyData,
      statusDistribution,
      responseTimeData,
      monthlyData,
      topAgents,
      peakHours,
      usageData: usageMetrics
    };
  };

  const createSecret = async () => {
    if (!newSecret.name || !newSecret.value) {
      setError('Secret name and value are required');
      return;
    }

    try {
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Create secret in ElevenLabs
      const response = await fetch('https://api.elevenlabs.io/v1/convai/secrets', {
        method: 'POST',
        headers: {
          'xi-api-key': apiKey,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newSecret.name,
          value: newSecret.value,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to create secret: ${response.statusText}`);
      }

      const secretData = await response.json();

      // Store tracking information in our database
      const { error: dbError } = await supabase
        .from('user_secrets')
        .insert({
          user_id: user.id,
          elevenlabs_secret_id: secretData.secret_id,
          name: newSecret.name
        });

      if (dbError) {
        console.error('Error storing secret tracking:', dbError);
        setError('Secret created but tracking failed. Please refresh if it doesn\'t appear.');
      }

      setNewSecret({ name: '', value: '' });
      setShowSecretForm(false);
      await fetchSecrets();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create secret');
    }
  };

  const deleteSecret = async (secretId: string) => {
    if (!window.confirm('Are you sure you want to delete this secret?')) return;

    try {
      // First verify the secret belongs to the user
      const { data: userSecret, error: dbError } = await supabase
        .from('user_secrets')
        .select('*')
        .eq('elevenlabs_secret_id', secretId)
        .single();

      if (dbError || !userSecret) {
        throw new Error('Secret not found or you do not have permission to delete it');
      }

      // Delete from ElevenLabs
      const response = await fetch(`https://api.elevenlabs.io/v1/convai/secrets/${secretId}`, {
        method: 'DELETE',
        headers: {
          'xi-api-key': apiKey,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to delete secret: ${response.statusText}`);
      }

      // Remove tracking information from our database
      const { error: deleteDbError } = await supabase
        .from('user_secrets')
        .delete()
        .eq('elevenlabs_secret_id', secretId);

      if (deleteDbError) {
        console.error('Error removing secret tracking:', deleteDbError);
        setError('Secret deleted but tracking cleanup failed. Please refresh.');
      }

      await fetchSecrets();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete secret');
    }
  };

  // Removed API key test and status section per request

  useEffect(() => {
    if (apiKey) {
      fetchSecrets();
      fetchDashboardSettings();
      fetchAnalyticsData();
    }
  }, [apiKey, fetchSecrets, fetchDashboardSettings, fetchAnalyticsData]);

  return (
    <div className="p-8 space-y-8">
      <div className="text-center mb-12">
        <h2 className="text-3xl font-bold text-white mb-4">Settings</h2>
        <p className="text-gray-400 text-lg">Configure your platform settings and manage secrets</p>
      </div>

      {error && (
        <div className="glass-card rounded-2xl p-4 mb-6 border border-red-500/30 neon-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <span className="text-red-400 text-xl">❌</span>
              <p className="text-red-300">{error}</p>
            </div>
            <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300">×</button>
          </div>
        </div>
      )}

      {/* Analytics Dashboard */}
      {apiKey && (
        <div className="space-y-8">
          <div className="glass-card rounded-2xl p-8">
            <div className="flex justify-between items-center mb-8">
              <div>
                <h3 className="text-3xl font-bold text-white flex items-center">
                  <span className="text-4xl mr-4">📊</span>
                  <span>Analytics Dashboard</span>
                </h3>
              </div>
              <button
                onClick={fetchAnalyticsData}
                className="btn-primary flex items-center space-x-2"
              >
                <span>🔄</span>
                <span>Refresh Data</span>
              </button>
            </div>
            
            {analyticsData.loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
                <p className="text-gray-300 text-lg">Loading analytics data...</p>
              </div>
            ) : !analyticsData.metrics ? (
              <div className="text-center py-12 text-gray-400">
                <div className="text-6xl mb-4">📊</div>
                <h4 className="text-lg font-medium text-gray-300 mb-2">No data available</h4>
                <p>Analytics will appear here once you have conversation data.</p>
              </div>
            ) : (
              <>
                {/* Key Metrics Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-12">
                  <div className="glass-effect rounded-xl p-6 border border-blue-500/30">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                        <span className="text-white text-xl">📞</span>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-white">{analyticsData.metrics.totalCalls.toLocaleString()}</div>
                        <div className="text-sm text-gray-400">
                          Total calls
                        </div>
                      </div>
                    </div>
                    <h4 className="text-gray-300 font-medium">Total Calls</h4>
                    <p className="text-gray-400 text-sm">All time</p>
                  </div>

                  <div className="glass-effect rounded-xl p-6 border border-green-500/30">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center">
                        <span className="text-white text-xl">✅</span>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-white">{analyticsData.metrics.successRate.toFixed(1)}%</div>
                        <div className="text-sm text-green-400">
                          Success rate
                        </div>
                      </div>
                    </div>
                    <h4 className="text-gray-300 font-medium">Success Rate</h4>
                    <p className="text-gray-400 text-sm">Completed calls</p>
                  </div>

                  <div className="glass-effect rounded-xl p-6 border border-purple-500/30">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                        <span className="text-white text-xl">⏱️</span>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-white">
                          {Math.floor(analyticsData.metrics.averageDuration / 60)}:{(analyticsData.metrics.averageDuration % 60).toFixed(0).padStart(2, '0')}
                        </div>
                        <div className="text-sm text-gray-400">
                          Average duration
                        </div>
                      </div>
                    </div>
                    <h4 className="text-gray-300 font-medium">Avg Duration</h4>
                    <p className="text-gray-400 text-sm">Per conversation</p>
                  </div>

                  {analyticsData.metrics.usageData.totalCredits > 0 && (
                    <div className="glass-effect rounded-xl p-6 border border-yellow-500/30">
                      <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 bg-yellow-500 rounded-xl flex items-center justify-center">
                          <span className="text-white text-xl">💳</span>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-white">{analyticsData.metrics.usageData.totalCredits.toLocaleString()}</div>
                          <div className="text-sm text-yellow-400">
                            Total credits
                          </div>
                        </div>
                      </div>
                      <h4 className="text-gray-300 font-medium">Total Credits</h4>
                      <p className="text-gray-400 text-sm">Last 30 days</p>
                    </div>
                  )}

                  {analyticsData.metrics.usageData.averageCreditsPerCall > 0 && (
                    <div className="glass-effect rounded-xl p-6 border border-indigo-500/30">
                      <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center">
                          <span className="text-white text-xl">🎤</span>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-white">{analyticsData.metrics.usageData.averageCreditsPerCall.toFixed(1)}</div>
                          <div className="text-sm text-indigo-400">
                            Credits per call
                          </div>
                        </div>
                      </div>
                      <h4 className="text-gray-300 font-medium">Credits per Call</h4>
                      <p className="text-gray-400 text-sm">Last 30 days</p>
                    </div>
                  )}
                </div>

                {/* Charts Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Call Volume Chart */}
                  <div className="glass-effect rounded-xl p-6">
                    <h4 className="text-xl font-bold text-white mb-6 flex items-center">
                      <span className="text-2xl mr-3">📈</span>
                      Call Volume Trend (Last 7 Days)
                    </h4>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={analyticsData.metrics.dailyData}>
                          <defs>
                            <linearGradient id="callsGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1}/>
                            </linearGradient>
                            <linearGradient id="successGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
                              <stop offset="95%" stopColor="#10b981" stopOpacity={0.1}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                          <XAxis dataKey="day" stroke="#9ca3af" />
                          <YAxis stroke="#9ca3af" />
                          <Tooltip 
                            contentStyle={{
                              backgroundColor: 'rgba(0, 0, 0, 0.8)',
                              border: '1px solid rgba(255, 255, 255, 0.1)',
                              borderRadius: '12px',
                              color: '#fff'
                            }}
                          />
                          <Area 
                            type="monotone" 
                            dataKey="calls" 
                            stroke="#3b82f6" 
                            fillOpacity={1} 
                            fill="url(#callsGradient)" 
                            name="Total Calls"
                          />
                          <Area 
                            type="monotone" 
                            dataKey="successful" 
                            stroke="#10b981" 
                            fillOpacity={1} 
                            fill="url(#successGradient)" 
                            name="Successful"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Call Status Distribution */}
                  {analyticsData.metrics.statusDistribution.length > 0 && (
                    <div className="glass-effect rounded-xl p-6">
                      <h4 className="text-xl font-bold text-white mb-6 flex items-center">
                        <span className="text-2xl mr-3">📊</span>
                        Call Status Distribution
                      </h4>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={analyticsData.metrics.statusDistribution}
                              cx="50%"
                              cy="50%"
                              outerRadius={80}
                              dataKey="value"
                              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(1)}%`}
                            >
                              {analyticsData.metrics.statusDistribution.map((entry: any, index: number) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                            <Tooltip 
                              contentStyle={{
                                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                borderRadius: '12px',
                                color: '#fff'
                              }}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  )}

                  {/* Call Duration Analysis */}
                  {analyticsData.metrics.responseTimeData.length > 0 && (
                    <div className="glass-effect rounded-xl p-6">
                      <h4 className="text-xl font-bold text-white mb-6 flex items-center">
                        <span className="text-2xl mr-3">⚡</span>
                        Call Duration Analysis
                      </h4>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={analyticsData.metrics.responseTimeData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                            <XAxis dataKey="time" stroke="#9ca3af" />
                            <YAxis stroke="#9ca3af" />
                            <Tooltip 
                              contentStyle={{
                                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                borderRadius: '12px',
                                color: '#fff'
                              }}
                            />
                            <Bar 
                              dataKey="count" 
                              fill="url(#barGradient)"
                              radius={[4, 4, 0, 0]}
                            />
                            <defs>
                              <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.8}/>
                                <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.3}/>
                              </linearGradient>
                            </defs>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  )}

                  {/* Monthly Performance */}
                  <div className="glass-effect rounded-xl p-6">
                    <h4 className="text-xl font-bold text-white mb-6 flex items-center">
                      <span className="text-2xl mr-3">📅</span>
                      Monthly Call Volume
                    </h4>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={analyticsData.metrics.monthlyData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                          <XAxis dataKey="month" stroke="#9ca3af" />
                          <YAxis stroke="#9ca3af" />
                          <Tooltip 
                            contentStyle={{
                              backgroundColor: 'rgba(0, 0, 0, 0.8)',
                              border: '1px solid rgba(255, 255, 255, 0.1)',
                              borderRadius: '12px',
                              color: '#fff'
                            }}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="calls" 
                            stroke="#3b82f6" 
                            strokeWidth={3}
                            dot={{ fill: '#3b82f6', strokeWidth: 2, r: 6 }}
                            name="Total Calls"
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>

                {/* Usage Charts */}
                {analyticsData.metrics.usageData.totalCredits > 0 && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                    {/* Daily Credit Usage */}
                    <div className="glass-effect rounded-xl p-6">
                      <h4 className="text-xl font-bold text-white mb-6 flex items-center">
                        <span className="text-2xl mr-3">💳</span>
                        Daily Credit Usage (Last 7 Days)
                      </h4>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={analyticsData.metrics.usageData.dailyUsage}>
                            <defs>
                              <linearGradient id="creditsGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#6366f1" stopOpacity={0.8}/>
                                <stop offset="95%" stopColor="#6366f1" stopOpacity={0.1}/>
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                            <XAxis dataKey="date" stroke="#9ca3af" />
                            <YAxis stroke="#9ca3af" />
                            <Tooltip 
                              contentStyle={{
                                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                borderRadius: '12px',
                                color: '#fff'
                              }}
                            />
                            <Area 
                              type="monotone" 
                              dataKey="credits" 
                              stroke="#6366f1" 
                              fillOpacity={1} 
                              fill="url(#creditsGradient)" 
                              name="Credits Used"
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    {/* Voice Usage Breakdown */}
                    {analyticsData.metrics.usageData.voiceBreakdown.length > 0 && (
                      <div className="glass-effect rounded-xl p-6">
                        <h4 className="text-xl font-bold text-white mb-6 flex items-center">
                          <span className="text-2xl mr-3">🎙️</span>
                          Voice Usage Breakdown
                        </h4>
                        <div className="h-64">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={analyticsData.metrics.usageData.voiceBreakdown}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                              <XAxis 
                                dataKey="voice" 
                                stroke="#9ca3af" 
                                fontSize={12}
                                angle={-45}
                                textAnchor="end"
                                height={80}
                              />
                              <YAxis stroke="#9ca3af" />
                              <Tooltip 
                                contentStyle={{
                                  backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                  border: '1px solid rgba(255, 255, 255, 0.1)',
                                  borderRadius: '12px',
                                  color: '#fff'
                                }}
                              />
                              <Bar 
                                dataKey="credits" 
                                fill="url(#voiceGradient)"
                                radius={[4, 4, 0, 0]}
                              />
                              <defs>
                                <linearGradient id="voiceGradient" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.8}/>
                                  <stop offset="95%" stopColor="#06b6d4" stopOpacity={0.3}/>
                                </linearGradient>
                              </defs>
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Additional Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                  <div className="glass-effect rounded-xl p-6 border border-indigo-500/30">
                    <h5 className="text-lg font-semibold text-white mb-4 flex items-center">
                      <span className="text-xl mr-2">🎯</span>
                      Top Performing Agents
                    </h5>
                    <div className="space-y-3">
                      {analyticsData.metrics.topAgents.length === 0 ? (
                        <p className="text-gray-400 text-sm">No agent data available</p>
                      ) : (
                        analyticsData.metrics.topAgents.map((agent: any, index: number) => (
                          <div key={index} className="flex justify-between items-center">
                            <span className="text-gray-300">{agent.name}</span>
                            <span className="text-green-400 font-semibold">{agent.successRate}%</span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  <div className="glass-effect rounded-xl p-6 border border-pink-500/30">
                    <h5 className="text-lg font-semibold text-white mb-4 flex items-center">
                      <span className="text-xl mr-2">🕒</span>
                      Peak Hours
                    </h5>
                    <div className="space-y-3">
                      {analyticsData.metrics.peakHours.length === 0 ? (
                        <p className="text-gray-400 text-sm">No peak hour data available</p>
                      ) : (
                        analyticsData.metrics.peakHours.map((hour: any, index: number) => (
                          <div key={index} className="flex justify-between items-center">
                            <span className="text-gray-300">{hour.timeRange}</span>
                            <span className="text-blue-400 font-semibold">{hour.calls} calls</span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  {analyticsData.metrics.usageData.totalCredits > 0 && (
                    <div className="glass-effect rounded-xl p-6 border border-cyan-500/30">
                      <h5 className="text-lg font-semibold text-white mb-4 flex items-center">
                        <span className="text-xl mr-2">📊</span>
                        Usage Metrics
                      </h5>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-300">Total Credits Used</span>
                          <span className="text-indigo-400 font-semibold">{analyticsData.metrics.usageData.totalCredits.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-300">Average Credits per Call</span>
                          <span className="text-indigo-400 font-semibold">{analyticsData.metrics.usageData.averageCreditsPerCall.toFixed(1)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-300">Total Calls</span>
                          <span className="text-blue-400 font-semibold">{analyticsData.metrics.totalCalls.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* API Key section removed per request */}

      {/* Secrets Management */}
      {apiKey && (
        <div className="glass-card rounded-2xl p-8">
          <div className="flex justify-between items-center mb-8">
            <h3 className="text-2xl font-bold text-white flex items-center">
              <span className="text-3xl mr-4">🔐</span>
              Secrets Management
            </h3>
            <button
              onClick={() => setShowSecretForm(true)}
              className="btn-primary flex items-center space-x-2"
            >
              <span>+</span>
              <span>Add Secret</span>
            </button>
          </div>

          <p className="text-gray-400 mb-6">
            Manage secrets that can be used by your agents and tools.
          </p>

          {secrets.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <div className="text-6xl mb-4">🔐</div>
              <h4 className="text-lg font-medium text-gray-300 mb-2">No secrets configured</h4>
              <p>Add secrets to securely store API keys and credentials for your agents.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {secrets.map((secret) => (
                <div
                  key={secret.secret_id}
                  className="glass-effect rounded-xl p-6 flex justify-between items-start card-hover"
                >
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                        <span className="text-white text-lg">🔐</span>
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-white">{secret.name}</h4>
                        <p className="text-gray-400 text-sm font-mono">ID: {secret.secret_id}</p>
                      </div>
                    </div>
                    
                    {secret.used_by && (
                      <div className="flex flex-wrap gap-2">
                        {secret.used_by.tools && secret.used_by.tools.length > 0 && (
                          <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-500/20 border border-blue-500/30 text-blue-300 text-xs">
                            🛠️ {secret.used_by.tools.length} tool(s)
                          </span>
                        )}
                        {secret.used_by.agent_tools && secret.used_by.agent_tools.length > 0 && (
                          <span className="inline-flex items-center px-3 py-1 rounded-full bg-green-500/20 border border-green-500/30 text-green-300 text-xs">
                            🤖 {secret.used_by.agent_tools.length} agent tool(s)
                          </span>
                        )}
                        {secret.used_by.others && secret.used_by.others.length > 0 && (
                          <span className="inline-flex items-center px-3 py-1 rounded-full bg-purple-500/20 border border-purple-500/30 text-purple-300 text-xs">
                            📋 {secret.used_by.others.join(', ')}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => deleteSecret(secret.secret_id)}
                    className="glass-effect hover:bg-red-500/20 border border-red-500/30 text-red-300 hover:text-red-200 px-4 py-2 rounded-xl transition-all duration-300 ml-4"
                  >
                    🗑️ Delete
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Usage Guidelines */}
      <div className="glass-card rounded-2xl p-8 border border-blue-500/30">
        <h3 className="text-2xl font-bold text-white mb-6 flex items-center">
          <span className="text-3xl mr-4">💡</span>
          Getting Started
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-gray-300">
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">1</div>
              <div>
                <h4 className="font-semibold text-white">Create agents</h4>
                <p className="text-sm text-gray-400">Set up conversational AI agents with custom prompts</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">2</div>
              <div>
                <h4 className="font-semibold text-white">Add phone numbers</h4>
                <p className="text-sm text-gray-400">Connect Twilio numbers for voice calls</p>
              </div>
            </div>
          </div>
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">3</div>
              <div>
                <h4 className="font-semibold text-white">Upload knowledge</h4>
                <p className="text-sm text-gray-400">Add documents to enhance agent responses</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">4</div>
              <div>
                <h4 className="font-semibold text-white">Monitor conversations</h4>
                <p className="text-sm text-gray-400">Track agent performance and user interactions</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Secret Form Modal */}
      {showSecretForm && createPortal(
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="glass-card rounded-2xl max-w-md w-full border border-white/10">
            <div className="p-8">
              <div className="flex justify-between items-center mb-8">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">Add Secret</h2>
                  <p className="text-gray-400">Store sensitive data securely</p>
                </div>
                <button
                  onClick={() => setShowSecretForm(false)}
                  className="text-gray-400 hover:text-white text-2xl transition-colors hover:bg-white/10 rounded-lg p-2"
                >
                  ×
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <label htmlFor="secretName" className="block text-sm font-medium text-gray-300 mb-2">
                    Secret Name
                  </label>
                  <input
                    type="text"
                    id="secretName"
                    value={newSecret.name}
                    onChange={(e) => setNewSecret(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-4 py-3 bg-black/20 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter secret name"
                  />
                </div>

                <div>
                  <label htmlFor="secretValue" className="block text-sm font-medium text-gray-300 mb-2">
                    Secret Value
                  </label>
                  <input
                    type="password"
                    id="secretValue"
                    value={newSecret.value}
                    onChange={(e) => setNewSecret(prev => ({ ...prev, value: e.target.value }))}
                    className="w-full px-4 py-3 bg-black/20 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter secret value"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-4 pt-8 border-t border-white/10 mt-8">
                <button
                  onClick={() => setShowSecretForm(false)}
                  className="btn-secondary px-6 py-3"
                >
                  Cancel
                </button>
                <button
                  onClick={createSecret}
                  className="btn-primary px-6 py-3"
                >
                  🔐 Add Secret
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Settings; 